import { useRef, useState } from "react";
import {
  ChevronRight,
  Envelope,
  Github,
  Linkedin,
  SendFill,
} from "react-bootstrap-icons";
import emailjs from "@emailjs/browser";
import isEmail from "validator/lib/isEmail";
import astronaut from "../assets/astronaut.svg";
import { trackEvent } from "../utils/analytics";
import "../styles/contacts.css";

const quickContacts = [
  {
    title: "Email",
    value: "contact@motola.dev",
    href: "mailto:ron00003@gmail.com",
    icon: Envelope,
    external: false,
    eventName: "click_email",
  },
  {
    title: "LinkedIn",
    value: "linkedin.com/in/ron-motola",
    href: "https://www.linkedin.com/in/ron-motola/",
    icon: Linkedin,
    external: true,
    eventName: "click_linkedin",
  },
  {
    title: "GitHub",
    value: "github.com/ronM3",
    href: "https://github.com/ronM3",
    icon: Github,
    external: true,
    eventName: "click_github",
  },
];

const Contacts = () => {
  const [success, setSuccess] = useState(false);
  const [nameError, setNameError] = useState(false);
  const [emailError, setEmailError] = useState(false);
  const [msgError, setMsgError] = useState(false);
  const [errMsg, setErrorMsg] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const form = useRef(null);

  const sendEmail = async (e) => {
    e.preventDefault();
    setSuccess(false);
    setErrorMsg("");

    const emailJsPublicKey =
      process.env.REACT_APP_EMAILJS_PUBLIC_KEY ||
      process.env.REACT_APP_EMAIL_KEY;
    const emailJsServiceId = process.env.REACT_APP_EMAILJS_SERVICE_ID;
    const emailJsTemplateId = process.env.REACT_APP_EMAILJS_TEMPLATE_ID;

    if (name.trim() === "") {
      setNameError(true);
      return;
    }
    setNameError(false);
    if (!isEmail(email.trim())) {
      setEmailError(true);
      return;
    }
    setEmailError(false);
    if (message.trim() === "") {
      setMsgError(true);
      return;
    }
    setMsgError(false);

    if (!form.current) {
      setErrorMsg("Contact form is unavailable. Please refresh and try again.");
      return;
    }

    if (!emailJsPublicKey || !emailJsServiceId || !emailJsTemplateId) {
      setErrorMsg(
        "Contact form is not configured. Check the EmailJS values in .env."
      );
      return;
    }

    try {
      await emailjs.sendForm(
        emailJsServiceId,
        emailJsTemplateId,
        form.current,
        emailJsPublicKey
      );

      setSuccess(true);
      setName("");
      setEmail("");
      setMessage("");
    } catch (error) {
      const nextError =
        error?.text === "Account not found"
          ? "EmailJS rejected the configured public key. Update the EmailJS public key in .env."
          : error?.text ||
            "Message could not be sent. Check your EmailJS settings and try again.";

      setErrorMsg(nextError);
      console.error("EmailJS send failed", error);
    }
  };

  return (
    <section className="contactClosing" aria-labelledby="contact-title">
      <div className="contactClosing__layout">
        <div className="contactClosing__intro">
          <p className="contactClosing__eyebrow">LET'S CONNECT</p>
          <h2 className="contactClosing__title" id="contact-title">
            <span>Let's</span>
            <span>
              connect
              <span className="contactClosing__sparkle" aria-hidden="true" />
            </span>
          </h2>
          <p className="contactClosing__copy">
            If you'd like to discuss a role, collaboration, or an opportunity,
            feel free to reach out.
          </p>
        </div>

        <div className="contactClosing__visual" aria-hidden="true">
          <img
            className="contactClosing__astronaut"
            src={astronaut}
            alt=""
            aria-hidden="true"
          />
        </div>

        <div className="contactClosing__formCard">
          {success ? (
            <div
              className="contactClosing__success"
              role="status"
              aria-live="polite"
            >
              <p className="contactClosing__successEyebrow">Message sent</p>
              <h3>Thank you for reaching out.</h3>
              <p>
                Your message made it safely to my inbox. I appreciate you taking
                the time to write, and I will get back to you soon.
              </p>
              <button type="button" onClick={() => setSuccess(false)}>
                Send another message
              </button>
            </div>
          ) : (
            <form ref={form} onSubmit={sendEmail} noValidate>
              <h3 className="contactClosing__formTitle">Send a message</h3>

              <div className="contactClosing__field contactClosing__field--line">
                <label htmlFor="name">Name</label>
                <input
                  id="name"
                  placeholder="Your name"
                  type="text"
                  name="name"
                  value={name}
                  autoComplete="name"
                  aria-invalid={nameError}
                  aria-describedby={nameError ? "name-error" : undefined}
                  onChange={(e) => setName(e.target.value)}
                />
                {nameError && (
                  <span id="name-error" className="contactClosing__error">
                    The name field is required
                  </span>
                )}
              </div>

              <div className="contactClosing__field contactClosing__field--line">
                <label htmlFor="email">Email</label>
                <input
                  id="email"
                  placeholder="you@company.com"
                  type="email"
                  name="email"
                  value={email}
                  autoComplete="email"
                  aria-invalid={emailError}
                  aria-describedby={emailError ? "email-error" : undefined}
                  onChange={(e) => setEmail(e.target.value)}
                />
                {emailError && (
                  <span id="email-error" className="contactClosing__error">
                    Enter a valid email address
                  </span>
                )}
              </div>

              <div className="contactClosing__field">
                <label htmlFor="message">Message</label>
                <textarea
                  id="message"
                  placeholder="Tell me about your inquiry..."
                  name="message"
                  value={message}
                  aria-invalid={msgError}
                  aria-describedby={msgError ? "message-error" : undefined}
                  onChange={(e) => setMessage(e.target.value)}
                />
                {msgError && (
                  <span id="message-error" className="contactClosing__error">
                    The message field is required
                  </span>
                )}
              </div>

              <button type="submit" className="contactClosing__submit">
                <SendFill aria-hidden="true" />
                <span>Send message</span>
              </button>

              {errMsg && (
                <span className="contactClosing__error" role="alert">
                  {errMsg}
                </span>
              )}
            </form>
          )}
        </div>

        <div className="contactClosing__quick" aria-label="Quick contact">
          <p className="contactClosing__quickLabel">QUICK CONTACT</p>
          <div className="contactClosing__quickGrid">
            {quickContacts.map((item) => {
              const Icon = item.icon;
              return (
                <a
                  className="contactClosing__quickCard"
                  href={item.href}
                  key={item.title}
                  target={item.external ? "_blank" : undefined}
                  rel={item.external ? "noreferrer" : undefined}
                  aria-label={`${item.title}: ${item.value}`}
                  onClick={() => {
                    trackEvent(item.eventName, {
                      location: "contact_or_footer",
                    });
                  }}
                >
                  <span className="contactClosing__quickIcon" aria-hidden="true">
                    <Icon />
                  </span>
                  <span className="contactClosing__quickText">
                    <span>{item.title}</span>
                    <span>{item.value}</span>
                  </span>
                  <ChevronRight
                    className="contactClosing__quickArrow"
                    aria-hidden="true"
                  />
                </a>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contacts;
